export const INSIGHTS_PLACEHOLDER = "{{insights}}";
export const BOLT_INSIGHTS_LIST_ATTR = "data-bolt-insights-list";

const ENTITY_REPLACE =
  /(?:&lbrace;|&#123;)\s*(?:&lbrace;|&#123;)\s*insights\s*(?:&rbrace;|&#125;)\s*(?:&rbrace;|&#125;)/gi;
const PLACEHOLDER_REPLACE = /\{\{\s*insights\s*\}\}/gi;
const MONTHLY_INSIGHTS_PATTERN = /Monthly Insights[^\n<]*/i;

const MIN_NEEDLE_LENGTH = 15;

export function buildInsightsListHtml(approvedInsights) {
  if (!approvedInsights?.length) return "";

  const items = approvedInsights
    .map(
      (insight) =>
        `<li><strong>${escapeHtml(insight.headline)}.</strong> ${escapeHtml(insight.content)}</li>`
    )
    .join("");

  return `<ul ${BOLT_INSIGHTS_LIST_ATTR}="true">${items}</ul>`;
}

export function approvedInsightsFingerprint(approvedInsights) {
  return (approvedInsights || [])
    .map((insight) => `${insight.headline}\u0000${insight.content}`)
    .join("\u0001");
}

function insightNeedles(insights) {
  return (insights || [])
    .flatMap((insight) => [insight.headline, insight.content])
    .map((value) => (value || "").trim())
    .filter((value) => value.length >= MIN_NEEDLE_LENGTH);
}

function textMatchesNeedle(text, needles) {
  const value = (text || "").trim();
  if (value.length < MIN_NEEDLE_LENGTH) return false;

  return needles.some((needle) => value.includes(needle) || needle.includes(value));
}

// Removes every previously injected insight element (any approval status) so the
// body returns to a clean slate before the current approved list is injected.
// This does not depend on a marker attribute (Trix strips those on re-render).
function stripInjectedInsightElements(doc, needles) {
  if (!needles.length) return;

  doc.body.querySelectorAll(`[${BOLT_INSIGHTS_LIST_ATTR}]`).forEach((el) => el.remove());

  [...doc.body.querySelectorAll("li, p, div, blockquote")].forEach((el) => {
    if (!el.isConnected) return;
    if (el.querySelector("li, p, div, blockquote, ul, ol")) return; // only leaf blocks
    if (textMatchesNeedle(el.textContent, needles)) el.remove();
  });

  // Drop lists emptied by the removals above.
  [...doc.body.querySelectorAll("ul, ol")].forEach((el) => {
    if (!el.querySelector("li")) el.remove();
  });

  // Drop wrappers that are now completely empty (keep Trix blank-line <div><br></div>).
  [...doc.body.querySelectorAll("div, p")].forEach((el) => {
    if (el.children.length === 0 && !el.textContent.trim()) el.remove();
  });
}

function normalizeToSinglePlaceholder(innerHtml) {
  let result = (innerHtml || "").replace(ENTITY_REPLACE, INSIGHTS_PLACEHOLDER);

  let seen = false;
  result = result.replace(PLACEHOLDER_REPLACE, () => {
    if (seen) return "";
    seen = true;
    return INSIGHTS_PLACEHOLDER;
  });

  if (seen) return result;

  if (MONTHLY_INSIGHTS_PATTERN.test(result)) {
    return result.replace(MONTHLY_INSIGHTS_PATTERN, (line) => `${line}<br>${INSIGHTS_PLACEHOLDER}`);
  }

  return `${result}<br>${INSIGHTS_PLACEHOLDER}`;
}

// Idempotent rebuild: no matter how polluted the editor body is, this clears all
// injected insight copies, guarantees a single anchor under the Monthly Insights
// heading, and renders the current approved insights there. Anything the user
// types (including content after the signature) is preserved.
export function rebuildComposeBody(html, approvedInsights, allInsights) {
  const doc = new DOMParser().parseFromString(html || "", "text/html");

  stripInjectedInsightElements(doc, insightNeedles(allInsights));

  let inner = normalizeToSinglePlaceholder(doc.body.innerHTML);

  const listHtml = buildInsightsListHtml(approvedInsights);
  if (listHtml) {
    inner = inner.replace(PLACEHOLDER_REPLACE, listHtml);
  }

  return inner;
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
